import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { cn } from '@/lib/cn';
import { LoadingSpinner } from '@/components/common';
import { Pagination } from './Pagination';

export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  /**
   * Server-reported total row count for pagination math. When omitted, falls
   * back to `data.length` (client-only pagination). With server-side
   * pagination, `data.length` only reflects the current page, which would pin
   * total pages to 1 and disable next/prev — pass the server total instead.
   */
  totalRows?: number;
  isLoading?: boolean;
  rowClick?: (row: T) => void;
  /**
   * Optional per-row HTML attributes. Useful for adding `data-testid` or
   * other identifiers to individual rows. When omitted, no extra attributes
   * are applied.
   */
  getRowProps?: (row: T) => Record<string, unknown>;
  className?: string;
}

export function DataTable<T>({
  columns,
  data,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  totalRows,
  isLoading = false,
  rowClick,
  getRowProps,
  className,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sorting ?? [],
      ...(pagination && {
        pagination: {
          pageIndex: pagination.pageIndex - 1,
          pageSize: pagination.pageSize,
        },
      }),
    },
    onSortingChange: onSortingChange
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(sorting ?? []) : updater;
          onSortingChange(next);
        }
      : undefined,
    onPaginationChange: onPaginationChange
      ? (updater) => {
          const prev = pagination ?? { pageIndex: 1, pageSize: 10 };
          const tanstackPrev = {
            pageIndex: prev.pageIndex - 1,
            pageSize: prev.pageSize,
          };
          const next = typeof updater === 'function' ? updater(tanstackPrev) : updater;
          onPaginationChange({
            pageIndex: next.pageIndex + 1,
            pageSize: next.pageSize,
          });
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Server-paginated tables (those that pass `totalRows`) ship one page in
    // `data` and own pagination + sort state on the server. Manual mode tells
    // TanStack to use `state.pagination` / `state.sorting` verbatim and skip
    // both the client-side row slicing and the autoreset hook that fires
    // resetPageIndex whenever `data` changes — that hook was driving the
    // freeze cycle: data swap -> autoreset -> onPaginationChange -> store
    // setPage(1) -> URL replace -> fetch -> data swap.
    //
    // Client-only tables (no `totalRows`) keep TanStack's row slicing and
    // client sort, so RevenueBreakdown / AuditLogTab / IncomingList behave
    // exactly as before.
    manualPagination: totalRows !== undefined,
    manualSorting: totalRows !== undefined,
    ...(pagination &&
      totalRows === undefined && {
        getPaginationRowModel: getPaginationRowModel(),
      }),
  });

  return (
    <div className={cn('w-full', className)}>
      <div className="overflow-hidden rounded-[10px] bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
        <table className="w-full border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'border-b border-[#eee] bg-[#fafafa] px-3.5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.5px] text-[#999]',
                        canSort && 'cursor-pointer select-none',
                      )}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      aria-sort={
                        sorted === 'asc'
                          ? 'ascending'
                          : sorted === 'desc'
                            ? 'descending'
                            : canSort
                              ? 'none'
                              : undefined
                      }
                    >
                      <span className="inline-flex items-center gap-1">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="text-[10px]" aria-hidden="true">
                            {sorted === 'asc' ? '\u2191' : sorted === 'desc' ? '\u2193' : '\u2195'}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="py-16 text-center">
                  <LoadingSpinner size="md" text="Loading data…" />
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-[13px] text-[#999]">
                  No data available
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => {
                const extraProps = (getRowProps ? getRowProps(row.original) : {}) as Record<
                  string,
                  unknown
                >;
                const extraClassName =
                  typeof extraProps.className === 'string' ? extraProps.className : undefined;
                // Spread consumer props first so DataTable's internal className
                // (merged via cn) and the onClick wired from `rowClick` always
                // win; otherwise a stray `onClick` / `className` in
                // `getRowProps` would silently break row click and styling.
                return (
                  <tr
                    key={row.id}
                    {...extraProps}
                    className={cn(
                      'border-b border-[#f2f2f2] transition-colors duration-[120ms]',
                      'hover:bg-[#fafafa]',
                      rowClick && 'cursor-pointer',
                      extraClassName,
                    )}
                    {...(rowClick ? { onClick: () => rowClick(row.original) } : {})}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3.5 py-[13px] text-[13px] align-middle">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pagination && onPaginationChange && (
        <Pagination
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          totalRows={totalRows ?? data.length}
          onPageChange={(page) => onPaginationChange({ ...pagination, pageIndex: page })}
          onPageSizeChange={(size) => onPaginationChange({ pageIndex: 1, pageSize: size })}
          className="mt-2"
        />
      )}
    </div>
  );
}
